import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Candle, Tick, DataMode, BacktestConfig, SignalMarker, PinningVolumeAnalysis, WholeCandleAnalysis, FVGZone, TrailingOHLC } from '@shared/types';
import { useSymbolStore } from './symbolStore';
import { findBarIndexAtOrAfter } from '../utils/findBarAt';

// Extended candle type with optional trailing OHLC data and pre-calculated indicators
// This is what the store actually stores - candles may have trailing and indicators attached
export interface ExtendedCandle extends Candle {
  trailing?: TrailingOHLC;
  // Pre-calculated indicators from backend (used instead of local calculation)
  ema9?: number;
  ema21?: number;
  ema200?: number;
  vwap?: number;
}

// Get default base symbol from registry (e.g., "MNQ", "SIL")
const getDefaultBaseSymbol = (): string => {
  const symbols = useSymbolStore.getState().symbols;
  if (symbols.length > 0) {
    return symbols[0].base;
  }
  return 'MNQ'; // Ultimate fallback
};

export interface MagnetMarker {
  timestamp: number;
  symbol: string;
  price: number;
  direction: 'bullish' | 'bearish';
  wickPct: number;
  valid: boolean;
  range?: number;
  invalidatedAt: number | null;
}

// Price levels data structure (from backend WebSocket)
export interface PriceLevelsData {
  sessionHigh: number;
  sessionLow: number;
  rthHigh: number;
  rthLow: number;
  prevDayHigh: number;
  prevDayLow: number;
  ibHigh: number;
  ibLow: number;
  midnightOpen: number;
  ibComplete: boolean;
  prevWeekHigh: number;
  prevWeekLow: number;
  currWeekHigh: number;
  currWeekLow: number;
  prevMonthHigh: number;
  prevMonthLow: number;
}

// Session types from cme-market-hours microservice
export type SessionType = 'closed' | 'maintenance' | 'asian' | 'european' | 'us-premarket' | 'rth' | 'us-postmarket';

// Market hours status from the microservice
export interface MarketHoursStatus {
  isOpen: boolean;
  session: SessionType;
  sessionName: string;
  isRTH: boolean;
  isETH: boolean;
  isMaintenance: boolean;
  currentTime: {
    ct: string;
    et: string;
    utc: string;
    timestamp: number;
  };
  nextEvent: {
    type: string;
    timestamp: number;
    description: string;
    timeUntil: number;
  };
  todaySchedule: Array<{
    time: string;
    event: string;
    type: string;
  }>;
  upcomingHoliday: {
    name: string;
    date: string;
    isEarlyClose: boolean;
    closeTime?: string;
  } | null;
  nextHoliday?: {
    name: string;
    date: string;
    isEarlyClose: boolean;
    closeTime?: string;
  } | null;
}

interface MarketState {
  // Current market data
  currentPrice: number;
  currentTick: Tick | null;
  candles: ExtendedCandle[];  // Candles may include trailing OHLC data
  candlesVersion: number;     // Bumped on catch-up to force chart setData()
  /**
   * Bumped whenever `candles` changes in a way that is NOT "the last bar was
   * overwritten" or "one bar was appended".
   *
   * This is the contract that lets a consumer treat `candles.slice(0, -1)` as
   * unchanged while only ticks are arriving: within one epoch the array is
   * append-only apart from its final element, so for a given
   * `(candlesEpoch, candles.length)` every element below `length - 1` is
   * guaranteed to be the same object it was before.
   *
   * Bulk replacements (`setCandles`), history loads (`prependCandles`), symbol
   * switches, resets and mid-series inserts all bump it. Deliberately NOT
   * bumped by the two tick fast paths — that is the entire point.
   */
  candlesEpoch: number;
  symbol: string;

  // Connection status
  isConnected: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'connecting';
  isLoading: boolean;

  // Market hours status (legacy)
  isMarketOpen: boolean;
  marketStatus: 'OPEN' | 'CLOSED';

  // Market hours from microservice
  marketHoursStatus: MarketHoursStatus | null;
  currentSession: SessionType;
  sessionName: string;
  isRTH: boolean;
  isETH: boolean;
  isMaintenance: boolean;
  upcomingHoliday: {
    name: string;
    date: string;
    isEarlyClose: boolean;
    closeTime?: string;
  } | null;
  nextHoliday: {
    name: string;
    date: string;
    isEarlyClose: boolean;
    closeTime?: string;
  } | null;
  nextEvent: {
    type: string;
    timestamp: number;
    description: string;
    timeUntil: number;
  } | null;

  // Backtest state
  dataMode: DataMode;
  backtestConfig: BacktestConfig | null;
  isBacktestLoading: boolean;

  // Lazy loading state for historical data
  isLoadingOlderData: boolean;
  hasMoreOlderData: boolean;

  // Signal markers from backend strategy
  signalMarkers: SignalMarker[];
  // Markers version for staleness detection
  markersVersion: number;

  // Magnet candle strategy markers
  magnetMarkers: MagnetMarker[];

  // Daily Sweep strategy markers

  // ICT strategy zones
  fvgZones: FVGZone[];

  // Pinning Volume analysis cache (keyed by "symbol:timestamp" for multi-chart support)
  pinningVolumeAnalysisCache: Map<string, PinningVolumeAnalysis>;

  // Whole Candle analysis cache (keyed by "symbol:timestamp" for multi-chart support)
  wholeCandleAnalysisCache: Map<string, WholeCandleAnalysis>;

  // Session high/low per symbol (triggers re-renders when data arrives from WebSocket)
  sessionHighLowBySymbol: Map<string, { high: number; low: number }>;

  // Price levels per symbol (RTH HOD/LOD, PDH/PDL, IB, Midnight Open)
  priceLevelsBySymbol: Map<string, PriceLevelsData>;

  // Actions
  setCurrentPrice: (price: number) => void;
  setCurrentTick: (tick: Tick) => void;
  updateOrAddCandle: (candle: ExtendedCandle) => void;
  setCandles: (candles: ExtendedCandle[]) => void;
  bumpCandlesVersion: () => void;
  setSymbol: (symbol: string) => void;
  switchSymbol: (symbol: string) => void;
  setConnected: (connected: boolean) => void;
  setConnectionStatus: (status: 'connected' | 'disconnected' | 'connecting') => void;
  setLoading: (loading: boolean) => void;
  setMarketOpen: (isOpen: boolean) => void;
  setMarketStatus: (status: 'OPEN' | 'CLOSED') => void;
  setDataMode: (mode: DataMode) => void;
  setBacktestConfig: (config: BacktestConfig | null) => void;
  setBacktestLoading: (loading: boolean) => void;
  // Lazy loading actions
  setLoadingOlderData: (loading: boolean) => void;
  setHasMoreOlderData: (hasMore: boolean) => void;
  prependCandles: (candles: ExtendedCandle[]) => void;
  setSignalMarkers: (markers: SignalMarker[]) => void;
  setMarkersVersion: (version: number) => void;
  addSignalMarker: (marker: SignalMarker) => void;
  clearSignalMarkers: () => void;
  // Magnet candle marker actions
  setMagnetMarkers: (markers: MagnetMarker[]) => void;
  addMagnetMarker: (marker: MagnetMarker) => void;
  // Daily Sweep marker actions
  setFVGZones: (zones: FVGZone[]) => void;
  addFVGZone: (zone: FVGZone) => void;
  updateFVGZone: (zone: FVGZone) => void;
  clearFVGZones: () => void;
  setPinningVolumeAnalysis: (symbol: string, timestamp: number, analysis: PinningVolumeAnalysis) => void;
  getPinningVolumeAnalysis: (symbol: string, timestamp: number) => PinningVolumeAnalysis | undefined;
  clearPinningVolumeAnalysisCache: () => void;
  setWholeCandleAnalysis: (symbol: string, timestamp: number, analysis: WholeCandleAnalysis) => void;
  getWholeCandleAnalysis: (symbol: string, timestamp: number) => WholeCandleAnalysis | undefined;
  clearWholeCandleAnalysisCache: () => void;

  // Market hours actions
  setMarketHoursStatus: (status: MarketHoursStatus) => void;
  setSessionInfo: (info: { previousSession?: SessionType; currentSession: SessionType; isRTH: boolean; isETH: boolean }) => void;
  setRTHActive: (isActive: boolean) => void;
  setUpcomingHoliday: (holiday: { name: string; date: string; isEarlyClose: boolean; closeTime?: string } | null) => void;

  // Session high/low actions
  setSessionHighLow: (symbol: string, high: number, low: number) => void;
  getSessionHighLow: (symbol: string) => { high: number; low: number };

  // Price levels actions
  setPriceLevels: (symbol: string, levels: PriceLevelsData) => void;
  getPriceLevels: (symbol: string) => PriceLevelsData | undefined;

  reset: () => void;
}

export const useMarketStore = create<MarketState>()(
  subscribeWithSelector((set) => ({
  currentPrice: 0,
  currentTick: null,
  candles: [],
  candlesVersion: 0,
  candlesEpoch: 0,
  symbol: getDefaultBaseSymbol(),
  isConnected: false,
  connectionStatus: 'disconnected',
  isLoading: false,
  isMarketOpen: false,
  marketStatus: 'CLOSED',

  // Market hours from microservice
  marketHoursStatus: null,
  currentSession: 'closed',
  sessionName: 'Closed',
  isRTH: false,
  isETH: false,
  isMaintenance: false,
  upcomingHoliday: null,
  nextHoliday: null,
  nextEvent: null,

  dataMode: 'live',
  backtestConfig: null,
  isBacktestLoading: false,
  isLoadingOlderData: false,
  hasMoreOlderData: true, // Assume more data available until we get a response with fewer candles
  signalMarkers: [],
  markersVersion: 0,
  magnetMarkers: [],
  fvgZones: [],
  pinningVolumeAnalysisCache: new Map(),
  wholeCandleAnalysisCache: new Map(),
  sessionHighLowBySymbol: new Map(),
  priceLevelsBySymbol: new Map(),

  setCurrentPrice: (price: number) =>
    set({ currentPrice: price }),

  setCurrentTick: (tick: Tick) =>
    set({ currentTick: tick, currentPrice: tick.price }),

  /**
   * Fold one candle into the series, keeping it sorted ascending.
   *
   * The series MUST stay sorted: lightweight-charts throws on out-of-order
   * data. That guarantee used to come from re-sorting the whole array on every
   * insert; it now comes from where the candle is placed, which is the same
   * guarantee for a fraction of the work.
   *
   * This runs on EVERY tick — the forming bar is re-emitted continuously — so
   * the two cases that actually happen are separated out and cost O(1):
   *
   *   1. another tick on the forming bar  -> overwrite the last element
   *   2. a brand new bar                  -> append
   *
   * The old code reached both through `findIndex`, which scans from index 0
   * and therefore walked the ENTIRE loaded history to find an element sitting
   * at the very end. With 30 days of 2-minute bars that is ~21,600 predicate
   * calls per tick, and the lag scaled with how much history was loaded
   * (issue #344).
   *
   * Genuinely out-of-order and mid-series arrivals still work — they just fall
   * through to a binary search (the same `findBarAt` lower-bound search the
   * overlays use) and a splice, instead of everyone paying for them.
   *
   * Behaviour is identical to the old sort-everything version, including the
   * awkward corners:
   *
   * - `findIndex` matched the FIRST candle with a given timestamp. If the
   *   series somehow holds duplicate timestamps (only `setCandles` /
   *   `prependCandles` could introduce them; this function never does), the
   *   fast path would overwrite the LAST of the pair instead. Hence the
   *   `candles[len - 2]` check below, which sends that case to the binary
   *   search — whose lower bound is, by construction, the first match.
   * - `Array.prototype.sort` is stable, so inserting a new timestamp landed it
   *   before every strictly-greater element and after every smaller one. That
   *   is exactly `findBarIndexAtOrAfter`, so the splice position matches.
   */
  updateOrAddCandle: (candle: Candle) =>
    set((state) => {
      const prev = state.candles;
      const len = prev.length;

      if (len === 0) {
        return { candles: [candle] };
      }

      const lastTimestamp = prev[len - 1].timestamp;

      // Case 1: another tick on the bar that is still forming. The dominant
      // case by a wide margin. Guarded against a duplicated tail timestamp so
      // it cannot disagree with the first-match semantics described above.
      if (
        candle.timestamp === lastTimestamp &&
        (len < 2 || prev[len - 2].timestamp !== candle.timestamp)
      ) {
        const newCandles = prev.slice();
        newCandles[len - 1] = candle;
        return { candles: newCandles };
      }

      // Case 2: a new bar opened. The series is already ascending, so an
      // append keeps it ascending — no re-sort needed.
      if (candle.timestamp > lastTimestamp) {
        return { candles: [...prev, candle] };
      }

      // Case 3: a genuinely out-of-order or mid-series arrival. Rare, so it is
      // allowed to be the slow one — but it is still O(log n) to locate.
      //
      // Anything that lands here has touched the series somewhere other than
      // the tail, which breaks the "only the last bar moves" invariant that
      // `candlesEpoch` promises to consumers, so the epoch is bumped.
      const insertAt = findBarIndexAtOrAfter(prev, candle.timestamp);
      const newCandles = prev.slice();
      if (insertAt < len && prev[insertAt].timestamp === candle.timestamp) {
        newCandles[insertAt] = candle;
      } else {
        newCandles.splice(insertAt, 0, candle);
      }
      return { candles: newCandles, candlesEpoch: state.candlesEpoch + 1 };
    }),

  setCandles: (candles: Candle[]) =>
    set((state) => ({
      candles: [...candles].sort((a, b) => a.timestamp - b.timestamp),
      candlesEpoch: state.candlesEpoch + 1,
    })),

  bumpCandlesVersion: () =>
    set((state) => ({ candlesVersion: state.candlesVersion + 1 })),

  setSymbol: (symbol: string) => {
    // Update StrategyService's current symbol for per-symbol session data.
    //
    // Deliberately fire-and-forget so the setter doesn't block on a module
    // load, but it MUST carry a catch. Without one a failed chunk load is an
    // unhandled rejection: in the browser it's silent (and the strategy
    // service keeps working the previous symbol), and under vitest it kills
    // the run at teardown while the import is still in flight — which failed
    // a deploy with all 733 tests passing.
    import('../services/StrategyService')
      .then(({ strategyService }) => {
        strategyService.setCurrentSymbol(symbol);
      })
      .catch((e) => {
        console.warn('[MARKET] could not tell StrategyService about', symbol, e);
      });
    // Clear analysis caches when symbol changes to prevent cross-symbol data
    set({
      symbol,
      pinningVolumeAnalysisCache: new Map(),
      wholeCandleAnalysisCache: new Map(),
    });
  },

  switchSymbol: (symbol: string) => {
    import('../services/StrategyService')
      .then(({ strategyService }) => {
        strategyService.setCurrentSymbol(symbol);
      })
      .catch((e) => {
        console.warn('[MARKET] could not tell StrategyService about', symbol, e);
      });
    set((state) => ({
      symbol,
      candles: [],
      currentPrice: 0,
      currentTick: null,
      candlesVersion: state.candlesVersion + 1,
      candlesEpoch: state.candlesEpoch + 1,
      isLoading: true,
      hasMoreOlderData: true,
      isLoadingOlderData: false,
      signalMarkers: [],
      magnetMarkers: [],
      markersVersion: 0,
      fvgZones: [],
      pinningVolumeAnalysisCache: new Map(),
      wholeCandleAnalysisCache: new Map(),
    }));
  },

  setConnected: (connected: boolean) =>
    set({ isConnected: connected }),

  setConnectionStatus: (status: 'connected' | 'disconnected' | 'connecting') =>
    set({ isConnected: status === 'connected', connectionStatus: status }),

  setLoading: (loading: boolean) =>
    set({ isLoading: loading }),

  setMarketOpen: (isOpen: boolean) =>
    set({ isMarketOpen: isOpen, marketStatus: isOpen ? 'OPEN' : 'CLOSED' }),

  setMarketStatus: (status: 'OPEN' | 'CLOSED') =>
    set({ marketStatus: status, isMarketOpen: status === 'OPEN' }),

  setDataMode: (mode: DataMode) =>
    set({ dataMode: mode }),

  setBacktestConfig: (config: BacktestConfig | null) =>
    set({ backtestConfig: config }),

  setBacktestLoading: (loading: boolean) =>
    set({ isBacktestLoading: loading }),

  // Lazy loading actions
  setLoadingOlderData: (loading: boolean) =>
    set({ isLoadingOlderData: loading }),

  setHasMoreOlderData: (hasMore: boolean) =>
    set({ hasMoreOlderData: hasMore }),

  prependCandles: (candles: ExtendedCandle[]) =>
    set((state) => {
      if (candles.length === 0) return state;
      // Merge older candles with existing, avoiding duplicates
      const existingTimestamps = new Set(state.candles.map(c => c.timestamp));
      const newCandles = candles.filter(c => !existingTimestamps.has(c.timestamp));
      // Prepend new candles and sort
      const merged = [...newCandles, ...state.candles].sort((a, b) => a.timestamp - b.timestamp);
      return { candles: merged, candlesEpoch: state.candlesEpoch + 1 };
    }),

  setSignalMarkers: (markers: SignalMarker[]) =>
    set({ signalMarkers: markers }),

  setMarkersVersion: (version: number) =>
    set({ markersVersion: version }),

  addSignalMarker: (marker: SignalMarker) =>
    set((state) => ({
      signalMarkers: [...state.signalMarkers, marker],
    })),

  clearSignalMarkers: () =>
    set({ signalMarkers: [] }),

  // Magnet candle marker actions
  setMagnetMarkers: (markers: MagnetMarker[]) =>
    set({ magnetMarkers: markers }),

  addMagnetMarker: (marker: MagnetMarker) =>
    set((state) => ({
      magnetMarkers: [...state.magnetMarkers, marker],
    })),

  // Daily Sweep marker actions


  // ICT FVG zone actions
  setFVGZones: (zones: FVGZone[]) =>
    set({ fvgZones: zones }),

  addFVGZone: (zone: FVGZone) =>
    set((state) => ({
      fvgZones: [...state.fvgZones, zone],
    })),

  updateFVGZone: (zone: FVGZone) =>
    set((state) => ({
      fvgZones: state.fvgZones.map((z) => (z.id === zone.id ? zone : z)),
    })),

  clearFVGZones: () =>
    set({ fvgZones: [] }),

  setPinningVolumeAnalysis: (symbol: string, timestamp: number, analysis: PinningVolumeAnalysis) =>
    set((state) => {
      const normalizedSymbol = symbol.replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '').replace(/[A-Z]\d{1,2}$/i, '').toUpperCase();
      const cacheKey = `${normalizedSymbol}:${timestamp}`;
      const newCache = new Map(state.pinningVolumeAnalysisCache);
      newCache.set(cacheKey, analysis);
      // Limit cache size to 500 entries
      if (newCache.size > 500) {
        const oldestKey = newCache.keys().next().value;
        if (oldestKey !== undefined) {
          newCache.delete(oldestKey);
        }
      }
      return { pinningVolumeAnalysisCache: newCache };
    }),

  getPinningVolumeAnalysis: (symbol: string, timestamp: number) => {
    // This is a selector, not an action - we need to use getState
    return undefined; // Will be accessed via getState().pinningVolumeAnalysisCache.get(`${symbol}:${timestamp}`)
  },

  clearPinningVolumeAnalysisCache: () =>
    set({ pinningVolumeAnalysisCache: new Map() }),

  setWholeCandleAnalysis: (symbol: string, timestamp: number, analysis: WholeCandleAnalysis) =>
    set((state) => {
      const normalizedSymbol = symbol.replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '').replace(/[A-Z]\d{1,2}$/i, '').toUpperCase();
      const cacheKey = `${normalizedSymbol}:${timestamp}`;
      const newCache = new Map(state.wholeCandleAnalysisCache);
      newCache.set(cacheKey, analysis);
      // Limit cache size to 500 entries
      if (newCache.size > 500) {
        const oldestKey = newCache.keys().next().value;
        if (oldestKey !== undefined) {
          newCache.delete(oldestKey);
        }
      }
      return { wholeCandleAnalysisCache: newCache };
    }),

  getWholeCandleAnalysis: (symbol: string, timestamp: number) => {
    // This is a selector, not an action - access via getState().wholeCandleAnalysisCache.get(`${symbol}:${timestamp}`)
    return undefined;
  },

  clearWholeCandleAnalysisCache: () =>
    set({ wholeCandleAnalysisCache: new Map() }),

  // Market hours actions
  setMarketHoursStatus: (status: MarketHoursStatus) =>
    set({
      marketHoursStatus: status,
      isMarketOpen: status.isOpen,
      marketStatus: status.isOpen ? 'OPEN' : 'CLOSED',
      currentSession: status.session,
      sessionName: status.sessionName,
      isRTH: status.isRTH,
      isETH: status.isETH,
      isMaintenance: status.isMaintenance,
      upcomingHoliday: status.upcomingHoliday,
      nextHoliday: status.nextHoliday,
      nextEvent: status.nextEvent,
    }),

  setSessionInfo: (info: { previousSession?: SessionType; currentSession: SessionType; isRTH: boolean; isETH: boolean }) =>
    set({
      currentSession: info.currentSession,
      isRTH: info.isRTH,
      isETH: info.isETH,
    }),

  setRTHActive: (isActive: boolean) =>
    set({ isRTH: isActive }),

  setUpcomingHoliday: (holiday: { name: string; date: string; isEarlyClose: boolean; closeTime?: string } | null) =>
    set({ upcomingHoliday: holiday }),

  // Session high/low actions - per symbol storage for multi-chart support
  setSessionHighLow: (symbol: string, high: number, low: number) =>
    set((state) => {
      // Normalize symbol (e.g., "MNQH6.CME" -> "MNQ")
      const normalizedSymbol = symbol.replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '').replace(/[A-Z]\d{1,2}$/i, '').toUpperCase();
      const newMap = new Map(state.sessionHighLowBySymbol);
      newMap.set(normalizedSymbol, { high, low });
      return { sessionHighLowBySymbol: newMap };
    }),

  getSessionHighLow: (symbol: string) => {
    // This is accessed via selector: useMarketStore.getState().sessionHighLowBySymbol.get(symbol)
    return { high: 0, low: 999999 };
  },

  // Price levels actions - per symbol storage for multi-chart support
  setPriceLevels: (symbol: string, levels: PriceLevelsData) =>
    set((state) => {
      // Normalize symbol (e.g., "MNQH6.CME" -> "MNQ")
      const normalizedSymbol = symbol.replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '').replace(/[A-Z]\d{1,2}$/i, '').toUpperCase();
      const newMap = new Map(state.priceLevelsBySymbol);
      newMap.set(normalizedSymbol, levels);
      return { priceLevelsBySymbol: newMap };
    }),

  getPriceLevels: (_symbol: string) => {
    // This is accessed via selector: useMarketStore.getState().priceLevelsBySymbol.get(symbol)
    return undefined;
  },

  reset: () =>
    set((state) => ({
      currentPrice: 0,
      currentTick: null,
      candles: [],
      candlesEpoch: state.candlesEpoch + 1,
      isConnected: false,
      isLoading: false,
      isLoadingOlderData: false,
      hasMoreOlderData: true,
      marketHoursStatus: null,
      currentSession: 'closed',
      sessionName: 'Closed',
      isRTH: false,
      isETH: false,
      isMaintenance: false,
      upcomingHoliday: null,
      nextEvent: null,
      sessionHighLowBySymbol: new Map(),
      priceLevelsBySymbol: new Map(),
    })),
  }))
);
